import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { API_BASE_URL, adminFetch } from '@/lib/authFetch';
import { toast } from 'sonner';
import { RefreshCw, Users, Camera } from 'lucide-react';

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

interface FaceEventoRow {
  id: string;
  camera_id: number | string;
  camera_nome: string | null;
  data_hora: string | null;
  localizacao?: string | null;
  reconhecido_como?: string | null;
  pessoa_interesse_id?: number | null;
  confianca_pct?: number | null;
  tipo_origem?: string | null;
  imagem_base64: string | null;
}

interface RelatorioFacesResponse {
  changed?: boolean;
  ultimo_evento_id?: string | null;
  periodo_dias: number;
  desde: string;
  total_deteccoes_periodo: number;
  eventos_total: number;
  reconhecidos_count?: number;
  watchlist_count?: number;
  eventos_pagina: number;
  eventos_por_pagina: number;
  eventos_total_paginas: number;
  eventos_recentes: FaceEventoRow[];
}

const GRID_OPTIONS = [12, 24, 36, 48] as const;
const AUTO_REFRESH_MS = 8000;
const DEFAULT_GRID_SIZE = 24;
const DEFAULT_DIAS = '7';

function formatDataHoraCurta(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
}

function eventImageSrc(ev: FaceEventoRow | null | undefined): string | null {
  if (!ev?.imagem_base64) return null;
  return `data:image/jpeg;base64,${ev.imagem_base64}`;
}

const FaceCaptureReport = () => {
  const [diasInput, setDiasInput] = useState(DEFAULT_DIAS);
  const [filtroNome, setFiltroNome] = useState('');
  const debDias = useDebounced(diasInput, 450);
  const debFiltro = useDebounced(filtroNome, 450);

  const [gridSize, setGridSize] = useState<number>(DEFAULT_GRID_SIZE);
  const [reloadToken, setReloadToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RelatorioFacesResponse | null>(null);
  const [pulseNovaCaptura, setPulseNovaCaptura] = useState(false);
  const [capturaModal, setCapturaModal] = useState<FaceEventoRow | null>(null);

  const lastDebouncedQuery = useRef<{ d: string; f: string; g: number } | null>(null);
  const ultimoEventoIdRef = useRef<string | null>(null);
  const pulseTimerRef = useRef<number | null>(null);
  const fetchAbortRef = useRef<AbortController | null>(null);
  const fetchGenRef = useRef(0);

  const fetchData = useCallback(async (silent = false) => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const gen = ++fetchGenRef.current;

    if (!silent) setLoading(true);
    try {
      let d = parseInt(String(debDias).trim(), 10);
      if (Number.isNaN(d) || d < 1) d = 1;
      if (d > 366) d = 366;

      const params = new URLSearchParams();
      params.set('dias', String(d));
      params.set('pagina', '1');
      params.set('por_pagina', String(gridSize));
      params.set('max_imagens', String(gridSize));
      if (debFiltro.trim()) params.set('reconhecido', debFiltro.trim());
      if (silent && ultimoEventoIdRef.current) {
        params.set('ultimo_id', ultimoEventoIdRef.current);
      } else if (silent) {
        return;
      }

      const res = await adminFetch(
        `${API_BASE_URL}/admin/relatorio-faces-capturadas?${params.toString()}`,
        { signal: controller.signal }
      );

      if (gen !== fetchGenRef.current) return;

      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/admin/login';
          return;
        }
        throw new Error('Falha ao carregar capturas faciais');
      }

      const json = (await res.json()) as RelatorioFacesResponse;
      if (gen !== fetchGenRef.current) return;
      if (json.changed === false) return;

      const novoId = json.ultimo_evento_id ?? json.eventos_recentes?.[0]?.id ?? null;
      if (novoId) ultimoEventoIdRef.current = novoId;

      setData((prev) => {
        const prevId = prev?.ultimo_evento_id ?? prev?.eventos_recentes?.[0]?.id;
        if (silent && novoId && novoId !== prevId) {
          setPulseNovaCaptura(true);
          if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
          pulseTimerRef.current = window.setTimeout(() => setPulseNovaCaptura(false), 2000);
        }
        return json;
      });
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      if (gen !== fetchGenRef.current) return;
      if (!silent) toast.error(e instanceof Error ? e.message : 'Erro ao carregar');
    } finally {
      if (gen === fetchGenRef.current && !silent) setLoading(false);
    }
  }, [debDias, debFiltro, gridSize]);

  useEffect(() => {
    const prev = lastDebouncedQuery.current;
    const queryChanged =
      prev === null || prev.d !== debDias || prev.f !== debFiltro || prev.g !== gridSize;
    if (queryChanged) {
      lastDebouncedQuery.current = { d: debDias, f: debFiltro, g: gridSize };
      ultimoEventoIdRef.current = null;
    }
    void fetchData(false);
  }, [debDias, debFiltro, gridSize, reloadToken, fetchData]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void fetchData(true);
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [fetchData]);

  const capturas = data?.eventos_recentes ?? [];
  const ultimaCaptura = capturas[0] ?? null;
  const totalPeriodo = data?.total_deteccoes_periodo ?? 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        <Card className="bg-protec-dark-light border-white/10">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase text-zinc-500">Capturas no período</p>
            <p className="text-xl font-semibold text-white tabular-nums">{totalPeriodo}</p>
          </CardContent>
        </Card>
        <Card className="bg-protec-dark-light border-white/10">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase text-zinc-500">Watchlist</p>
            <p className="text-xl font-semibold text-white tabular-nums">{data?.watchlist_count ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className="bg-protec-dark-light border-white/10">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase text-zinc-500">Identificados</p>
            <p className="text-xl font-semibold text-white tabular-nums">{data?.reconhecidos_count ?? '—'}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-white text-base">Filtros</CardTitle>
            {data != null && !loading && (
              <span className="text-xs text-gray-400 tabular-nums">
                {totalPeriodo} captura(s) no período
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-4">
            <div className="flex flex-col gap-1.5 shrink-0 w-full sm:w-auto">
              <Label className="text-gray-300 text-sm">Período (dias)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={diasInput}
                onChange={(e) => setDiasInput(e.target.value.replace(/[^\d]/g, ''))}
                className="bg-protec-dark border-protec-gold/30 text-white h-10 w-full sm:w-28"
              />
            </div>
            <div className="flex flex-col gap-1.5 flex-1 min-w-0 max-w-md">
              <Label className="text-gray-300 text-sm">Nome reconhecido (opcional)</Label>
              <Input
                placeholder="Filtrar por nome..."
                value={filtroNome}
                onChange={(e) => setFiltroNome(e.target.value)}
                className="bg-protec-dark border-protec-gold/30 text-white h-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 min-h-[520px]">
        <Card
          className={`xl:col-span-5 rounded-xl border border-protec-gold/20 bg-gradient-to-b from-protec-dark-light to-zinc-950/80 ${
            pulseNovaCaptura ? 'ring-2 ring-protec-gold/60' : ''
          }`}
        >
          <CardHeader className="border-b border-protec-gold/10 pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-protec-gold" />
              Última captura
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {loading && !ultimaCaptura ? (
              <div className="aspect-[4/3] rounded-lg bg-zinc-800/70 animate-pulse" />
            ) : !ultimaCaptura ? (
              <div className="py-16 text-center text-gray-400">
                <Camera className="w-10 h-10 mx-auto mb-3 opacity-50" />
                Nenhuma captura facial no período.
              </div>
            ) : (
              <>
                {eventImageSrc(ultimaCaptura) ? (
                  <img
                    src={eventImageSrc(ultimaCaptura)!}
                    alt=""
                    className="w-full aspect-[4/3] object-contain rounded-lg bg-black/40 border border-protec-gold/10"
                  />
                ) : (
                  <div className="aspect-[4/3] rounded-lg bg-zinc-900 flex items-center justify-center">
                    <Camera className="w-12 h-12 text-zinc-600" />
                  </div>
                )}
                <p className="text-lg font-semibold text-white">
                  {ultimaCaptura.reconhecido_como || 'Rosto não identificado'}
                </p>
                {ultimaCaptura.confianca_pct != null && (
                  <Badge variant="outline" className="border-protec-gold/40 text-protec-gold">
                    {ultimaCaptura.confianca_pct}% confiança
                  </Badge>
                )}
                <p className="text-sm text-gray-400">{formatDataHoraCurta(ultimaCaptura.data_hora)}</p>
                <p className="text-sm text-gray-500">
                  {ultimaCaptura.camera_nome || `Câmera ${ultimaCaptura.camera_id}`}
                  {ultimaCaptura.localizacao ? ` · ${ultimaCaptura.localizacao}` : ''}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-7 rounded-xl border border-protec-gold/20 bg-gradient-to-b from-protec-dark-light to-zinc-950/80">
          <CardHeader className="border-b border-protec-gold/10 pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-white text-base">Grid de capturas</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={String(gridSize)} onValueChange={(v) => setGridSize(Number(v))}>
                  <SelectTrigger className="h-8 w-[72px] bg-protec-dark border-protec-gold/30 text-white text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-protec-dark border-protec-gold/30">
                    {GRID_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)} className="text-white text-xs">
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-protec-gold hover:bg-protec-gold/10"
                  onClick={() => setReloadToken((t) => t + 1)}
                  title="Atualizar"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {loading && capturas.length === 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-lg bg-zinc-800/60 animate-pulse" />
                ))}
              </div>
            ) : capturas.length === 0 ? (
              <p className="text-center text-gray-500 py-12">Nenhuma captura para exibir.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[640px] overflow-y-auto pr-1">
                {capturas.map((ev) => {
                  const src = eventImageSrc(ev);
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => setCapturaModal(ev)}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-protec-gold/50 focus:outline-none focus:ring-2 focus:ring-protec-gold/40"
                    >
                      {src ? (
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
                          <Users className="w-8 h-8 text-zinc-600" />
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-1.5">
                        <p className="text-[10px] text-white truncate">
                          {ev.reconhecido_como || formatDataHoraCurta(ev.data_hora)}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!capturaModal} onOpenChange={() => setCapturaModal(null)}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{capturaModal?.reconhecido_como || 'Captura facial'}</DialogTitle>
          </DialogHeader>
          {capturaModal && eventImageSrc(capturaModal) && (
            <img
              src={eventImageSrc(capturaModal)!}
              alt=""
              className="w-full rounded-lg border border-protec-gold/20"
            />
          )}
          {capturaModal && (
            <div className="text-sm text-gray-400 space-y-1">
              <p>{formatDataHoraCurta(capturaModal.data_hora)}</p>
              <p>{capturaModal.camera_nome || `Câmera ${capturaModal.camera_id}`}</p>
              {capturaModal.localizacao && <p>{capturaModal.localizacao}</p>}
              {capturaModal.confianca_pct != null && (
                <p>Confiança: {capturaModal.confianca_pct}%</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FaceCaptureReport;
